//* react
import React from 'react';

//* material-ui
import Grid from '@mui/material/Grid2';
import {
  Box,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Popover,
  Select,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

//* icons
import CloseIcon from '@mui/icons-material/Close';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import DisplaySettingsOutlinedIcon from '@mui/icons-material/DisplaySettingsOutlined';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';

//* types
import {
  TemplatesMenuFilters,
  TemplatesMenuFiltersParams,
  TemplatesMenuHeaderProps,
} from 'types/templates/templates-menu';

//* system
import SelectableIconButton from 'ui-component/extended/buttons/SelectableIconButton';
import TemplatesMenuApprovedOnlyInfo from 'views/common/templates/TemplatesMenuApprovedOnlyInfo';
import useLocalization from 'hooks/useLocalization';
import {
  CHAT_TOOLS_PANEL_ROW_HORIZONTAL_INSET_SPACING,
  CHAT_TRIPLEX_HEADER_INNER_MARGIN_BOTTOM,
  CHAT_TRIPLEX_HEADER_OUTER_PY,
  chatTriplexHeaderBorderColor,
  chatTriplexListPanelHeaderBg,
} from 'layout/MainLayout/layout-zoom-constants';
import { useSelector } from 'store';

const FILTER_ROW_HEIGHT_DEFAULT_PX = 40;

const TemplatesMenuHeader: React.FC<TemplatesMenuHeaderProps> = ({
  closable = false,
  onClose = () => {},
  chatUserName,
  filters,
  setFilters,
  disablePadding,
  isPriorityModal = false,
  categoryTemplateCounts = { marketing: 0, service: 0 },
  compact = false,
  embeddedInPanel = false,
}) => {
  //* localization
  const theme = useTheme();
  const filterRowHeightPx = React.useMemo(
    () => (compact ? parseFloat(theme.spacing(4)) : FILTER_ROW_HEIGHT_DEFAULT_PX),
    [compact, theme],
  );
  const { currentLocale } = useLocalization();
  const { localization } = React.useMemo(
    () => ({
      localization: currentLocale.templatesMenu,
    }),
    [currentLocale],
  );

  //* data & states
  const languages = useSelector((state) => state.templates.languages);
  const { whatsappTemplates } = useSelector((state) => state.templates);
  
  // Extract unique tags from all templates
  const availableTags = React.useMemo(() => {
    const allTags = new Set<string>();
    
    whatsappTemplates.forEach(template => {
      const templateLabels = (template.hebrewName as any)?.labels || [];
      templateLabels.forEach((labelName: string) => {
        if (labelName && labelName.trim()) {
          allTags.add(labelName.trim());
        }
      });
    });

    return Array.from(allTags).sort();
  }, [whatsappTemplates]);

  //* filters
  const [showFiltersAnchor, setShowFiltersAnchor] = React.useState<HTMLElement | null>(null);
  const { isHebrew, language, searchQuery, showMarketing, showService, showSecondaryTemplateName } = filters;

  const filtersParams = React.useMemo<TemplatesMenuFiltersParams>(
    () => ({
      isActive: [
        {
          value: true,
          label: localization.filters.activeStatusFilter.active,
        },
        {
          value: false,
          label: localization.filters.activeStatusFilter.notActive,
        },
      ],
      isHebrew: [
        {
          value: true,
          label: localization.filters.hebrewViewFilter.hebrew,
        },
        {
          value: false,
          label: localization.filters.hebrewViewFilter.notHebrew,
        },
      ],
      languages: languages.map((lang) => {
        return {
          value: lang,
          label: lang,
        };
      }),
      tags: availableTags.map((tag) => {
        return {
          value: tag,
          label: tag,
        };
      }),
      sortBy: [
        {
          value: 'lastUsed' as const,
          label: 'לפי תאריך יצירה',
        },
        {
          value: 'name' as const,
          label: 'שם מא-ת',
        },
      ],
      searchQuery: '',
    }),
    [
      availableTags,
      languages,
      localization.filters.activeStatusFilter.active,
      localization.filters.activeStatusFilter.notActive,
      localization.filters.hebrewViewFilter.hebrew,
      localization.filters.hebrewViewFilter.notHebrew,
    ],
  );

  //* Handlers
  const openFilters = (e: React.MouseEvent<HTMLElement>) => {
    const target = e.currentTarget;
    setShowFiltersAnchor(target);
  };
  const closeFilters = () => {
    setShowFiltersAnchor(null);
  };

  const handleFiltersParams = (params: Partial<TemplatesMenuFilters>) => {
    setFilters((prevFilters) => ({ ...prevFilters, ...params }));
  };
  const handleIsHebrewFilter = (value: boolean) => {
    handleFiltersParams({ isHebrew: value });
  };
  const handleLanguageFilter = (value: string[]) => {
    handleFiltersParams({ language: value });
  };
  const handleSearchFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters((prevFilters) => {
      return { ...prevFilters, searchQuery: e.target.value };
    });
  };
  const clearSearch = () => {
    handleSearchFilter({ target: { value: '' } } as React.ChangeEvent<HTMLInputElement>);
  };
  const handleTagsFilter = (value: string[]) => {
    handleFiltersParams({ selectedTags: value });
  };
  const handleSortByFilter = (value: 'lastUsed' | 'name') => {
    handleFiltersParams({ sortBy: value });
  };

  const lockMarketingOff = showMarketing && !showService;
  const lockServiceOff = showService && !showMarketing;

  const marketingCategoryTooltip = lockMarketingOff
    ? localization.filters.minOneCategoryTooltip
    : showMarketing
      ? localization.filters.categoryMarketing + ' — מוצג (לחץ להסתרה)'
      : localization.filters.categoryMarketing + ' — מוסתר (לחץ להצגה)';
  const serviceCategoryTooltip = lockServiceOff
    ? localization.filters.minOneCategoryTooltip
    : showService
      ? localization.filters.categoryService + ' — מוצג (לחץ להסתרה)'
      : localization.filters.categoryService + ' — מוסתר (לחץ להצגה)';

  const categoryToggleBaseSx = {
    minHeight: filterRowHeightPx,
    height: filterRowHeightPx,
    px: 0.875,
    gap: 0.5,
    borderRadius: 2,
    textTransform: 'none',
    fontWeight: 600,
    border: '1px solid',
    '& .MuiSvgIcon-root': { fontSize: 18 },
    '&:not(.Mui-selected)': {
      color: 'text.secondary',
      borderColor: 'divider',
      bgcolor: 'transparent',
      fontWeight: 500,
      '& .MuiSvgIcon-root': { color: 'text.secondary' },
      '&:hover': {
        bgcolor: 'action.hover',
        borderColor: 'text.disabled',
      },
    },
  } as const;

  return (
    <React.Fragment>
      {/* Header — שורה אחת: סינון קטגוריה (אייקון) · חיפוש · הגדרות תצוגה */}
      <Grid container spacing={0} sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
        <Grid size={12} id="templates-menu-settings-section" sx={{ minWidth: 0, maxWidth: '100%' }}>
          <Box
            sx={{
              /**
               * פאנל צ'אט: הרקע האפור נמתח לרוחב מלא; הריפוד האופקי רק על תוכן השורה (מעטפת פנימית).
               */
              width: '100%',
              boxSizing: 'border-box',
              pt:
                disablePadding && compact
                  ? theme.spacing(0.75)
                  : disablePadding
                    ? CHAT_TRIPLEX_HEADER_OUTER_PY
                    : 0,
              pb:
                disablePadding && compact
                  ? theme.spacing(0.75)
                  : disablePadding
                    ? CHAT_TRIPLEX_HEADER_OUTER_PY
                    : 0.75,
              px: embeddedInPanel ? 1 : disablePadding ? 0 : 2.5,
              borderBottom: '1px solid',
              borderColor: chatTriplexHeaderBorderColor(theme),
              ...(disablePadding
                ? {
                    mb: compact ? theme.spacing(0.75) : CHAT_TRIPLEX_HEADER_INNER_MARGIN_BOTTOM,
                    bgcolor: chatTriplexListPanelHeaderBg(theme),
                  }
                : {}),
              ...(isPriorityModal && {
                maxWidth: '80%',
                margin: '0 auto',
              }),
            }}
          >
            <Box
              style={
                disablePadding
                  ? {
                      paddingLeft: theme.spacing(CHAT_TOOLS_PANEL_ROW_HORIZONTAL_INSET_SPACING),
                      paddingRight: theme.spacing(CHAT_TOOLS_PANEL_ROW_HORIZONTAL_INSET_SPACING),
                      boxSizing: 'border-box',
                    }
                  : undefined
              }
              sx={{ width: '100%', minWidth: 0, maxWidth: '100%' }}
            >
            <Stack
              direction="row"
              alignItems="center"
              spacing={compact ? 0.5 : 1}
              sx={{
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                gap: compact ? 0.5 : 1,
              }}
            >
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0 }}>
                <Tooltip title={marketingCategoryTooltip} placement="bottom" enterDelay={lockMarketingOff ? 0 : 300}>
                  <span style={{ display: 'inline-flex' }}>
                    <ToggleButton
                      value="marketing"
                      size="small"
                      selected={showMarketing}
                      disabled={lockMarketingOff}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFiltersParams({ showMarketing: !showMarketing });
                      }}
                      aria-label={marketingCategoryTooltip}
                      aria-pressed={showMarketing}
                      sx={{
                        ...categoryToggleBaseSx,
                        '&.Mui-selected': {
                          bgcolor: 'primary.main',
                          color: 'common.white',
                          borderColor: 'primary.main',
                          '& .MuiSvgIcon-root': { color: 'common.white' },
                          '&:hover': {
                            bgcolor: 'primary.dark',
                            borderColor: 'primary.dark',
                            color: 'common.white',
                            '& .MuiSvgIcon-root': { color: 'common.white' },
                          },
                        },
                      }}
                    >
                      <CampaignOutlinedIcon aria-hidden />
                      <Box
                        component="span"
                        sx={{
                          minWidth: 20,
                          height: 20,
                          px: 0.5,
                          borderRadius: 1,
                          fontSize: 11,
                          fontWeight: 700,
                          lineHeight: '20px',
                          textAlign: 'center',
                          bgcolor: showMarketing ? 'rgba(255,255,255,0.22)' : 'action.selected',
                          color: showMarketing ? 'common.white' : 'text.secondary',
                        }}
                      >
                        {categoryTemplateCounts.marketing}
                      </Box>
                    </ToggleButton>
                  </span>
                </Tooltip>
                <Tooltip title={serviceCategoryTooltip} placement="bottom" enterDelay={lockServiceOff ? 0 : 300}>
                  <span style={{ display: 'inline-flex' }}>
                    <ToggleButton
                      value="service"
                      size="small"
                      selected={showService}
                      disabled={lockServiceOff}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFiltersParams({ showService: !showService });
                      }}
                      aria-label={serviceCategoryTooltip}
                      aria-pressed={showService}
                      sx={{
                        ...categoryToggleBaseSx,
                        '&.Mui-selected': {
                          bgcolor: 'secondary.main',
                          color: 'common.white',
                          borderColor: 'secondary.main',
                          '& .MuiSvgIcon-root': { color: 'common.white' },
                          '&:hover': {
                            bgcolor: 'secondary.dark',
                            borderColor: 'secondary.dark',
                            color: 'common.white',
                            '& .MuiSvgIcon-root': { color: 'common.white' },
                          },
                        },
                      }}
                    >
                      <NotificationsOutlinedIcon aria-hidden />
                      <Box
                        component="span"
                        sx={{
                          minWidth: 20,
                          height: 20,
                          px: 0.5,
                          borderRadius: 1,
                          fontSize: 11,
                          fontWeight: 700,
                          lineHeight: '20px',
                          textAlign: 'center',
                          bgcolor: showService ? 'rgba(255,255,255,0.22)' : 'action.selected',
                          color: showService ? 'common.white' : 'text.secondary',
                        }}
                      >
                        {categoryTemplateCounts.service}
                      </Box>
                    </ToggleButton>
                  </span>
                </Tooltip>
              </Stack>

              <TextField
                fullWidth
                variant="outlined"
                hiddenLabel
                placeholder={localization.inputPlaceholder}
                onChange={handleSearchFilter}
                value={searchQuery}
                size="small"
                sx={{
                  flex: '1 1 0%',
                  minWidth: 0,
                  alignSelf: 'center',
                  '& .MuiOutlinedInput-root': {
                    minHeight: filterRowHeightPx,
                    height: filterRowHeightPx,
                    boxSizing: 'border-box',
                    alignItems: 'center',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(theme.palette.divider, 0.45),
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(theme.palette.divider, 0.65),
                  },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                    borderWidth: '1px',
                  },
                  '& .MuiInputBase-input': {
                    py: 0,
                  },
                }}
                slotProps={{
                  htmlInput: {
                    id: 'templates-menu-search-input',
                    'aria-label': localization.inputPlaceholder,
                  },
                  input: {
                    startAdornment: (
                      <InputAdornment
                        position="start"
                        sx={{
                          marginInlineEnd: 1,
                          display: 'flex',
                          alignItems: 'center',
                          alignSelf: 'center',
                          height: 'auto',
                          maxHeight: 'none',
                        }}
                      >
                        <SearchOutlinedIcon
                          aria-hidden
                          sx={{ fontSize: 20, color: 'text.secondary', display: 'block' }}
                        />
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery.length > 0 && (
                      <InputAdornment position="end">
                        <IconButton onClick={clearSearch} aria-label="ניקוי חיפוש" size="small">
                          <CloseIcon sx={{ color: 'error.main' }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />

              <Box
                sx={{
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  alignSelf: 'center',
                  height: filterRowHeightPx,
                  gap: 0.25,
                }}
              >
                <TemplatesMenuApprovedOnlyInfo />
                <Tooltip title={localization.filters.viewSettingsTooltip} placement="bottom">
                  <SelectableIconButton
                    onClick={openFilters}
                    aria-label={localization.filters.header}
                  >
                    <DisplaySettingsOutlinedIcon />
                  </SelectableIconButton>
                </Tooltip>
              </Box>
            </Stack>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Filters */}
      <Popover
        open={Boolean(showFiltersAnchor)}
        onClose={() => closeFilters()}
        anchorEl={showFiltersAnchor}
        slotProps={{
          paper: {
            sx: {
              width: 320,
              //? mr: templatesWithSend ? 0.875 : -0.875,
              // mt: templatesWithSend ? 0.5 : 1.5,
              borderRadius: 1,
              borderColor: 'secondary.main',
              borderStyle: 'solid',
              borderWidth: '1px 1px 2px 2px',
            },
          },
        }}
        //? anchorOrigin={{
        //   vertical: 'top',
        //   horizontal: templatesWithSend ? 'right' : 'left',
        // }}
        //? transformOrigin={{
        //   vertical: 'top',
        //   horizontal: templatesWithSend ? 'left' : 'right',
        // }}
      >
        <Box
          sx={{
            px: 3.375,
            py: 1.375,
          }}
        >
          <Grid container spacing={0.5}>
            <Grid size={12}>
              <Typography>{localization.filters.header}</Typography>
            </Grid>
            <Grid size={12}>
              <Tooltip title={localization.filters.showSecondaryNameHint} placement="top">
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={showSecondaryTemplateName}
                      onChange={(e) =>
                        handleFiltersParams({ showSecondaryTemplateName: e.target.checked })
                      }
                      inputProps={{ 'aria-label': localization.filters.showSecondaryName }}
                    />
                  }
                  label={
                    <Typography variant="body2" component="span">
                      {localization.filters.showSecondaryName}
                    </Typography>
                  }
                  sx={{ m: 0, alignItems: 'center', gap: 0.75 }}
                />
              </Tooltip>
            </Grid>
            <Grid size={12}>
              <Grid container spacing={1.25} flexWrap={'nowrap'} alignItems={'center'}>
                <Grid size="grow">
                  <Typography>{localization.filters.hebrewViewFilter.toggleLanguageText}</Typography>
                </Grid>
                <Grid>
                  <FormControl fullWidth size="small" sx={{ width: 100 }}>
                    <Select
                      value={isHebrew}
                      onChange={(e) => handleIsHebrewFilter(e.target.value === 'true')}
                    >
                      {filtersParams.isHebrew.map((item) => (
                        <MenuItem value={String(item.value)} key={String(item.value)}>
                          {item.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>
            <Grid size={12}>
              <Grid container spacing={1.25} flexWrap={'nowrap'} alignItems={'center'}>
                <Grid size="grow">
                  <Typography>{localization.filters.languageFilter.label}</Typography>
                </Grid>
                <Grid>
                  <Tooltip
                    title={filtersParams.languages
                      .filter((lang) => language.includes(lang.value))
                      ?.map((lang) => lang.label)
                      .join(', ')}
                  >
                    <FormControl fullWidth size="small" sx={{ width: 100 }}>
                      <Select
                        value={language}
                        // onChange={(e) => handleLanguageFilter(e.target.value)}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value.includes('all') && !language.includes('all')) {
                            handleLanguageFilter(['all']);
                          } else {
                            handleLanguageFilter(
                              typeof value === 'string'
                                ? value.split(',').filter((valuePart) => valuePart !== 'all')
                                : value.filter((valuePart) => valuePart !== 'all'),
                            );
                          }
                        }}
                        multiple
                      >
                        <MenuItem value={'all'}>{localization.filters.languageFilter.all}</MenuItem>
                        {filtersParams.languages.map((item) => (
                          <MenuItem value={item.value} key={item.value}>
                            {item.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Tooltip>
                </Grid>
              </Grid>
            </Grid>
            {/* Tags Filter */}
            {availableTags.length > 0 && (
              <Grid size={12}>
                <Grid container spacing={1.25} flexWrap={'nowrap'} alignItems={'center'}>
                  <Grid size="grow">
                    <Typography>תגיות</Typography>
                  </Grid>
                  <Grid>
                    <FormControl fullWidth size="small" sx={{ width: 100 }}>
                      <Select
                        value={filters.selectedTags}
                        onChange={(e) => {
                          const value = e.target.value;
                          handleTagsFilter(
                            typeof value === 'string' ? value.split(',') : value
                          );
                        }}
                        multiple
                        displayEmpty
                        renderValue={(selected) => 
                          selected.length === 0 ? 'הכל' : `${selected.length} תגיות`
                        }
                      >
                        {filtersParams.tags.map((item) => (
                          <MenuItem value={item.value} key={item.value}>
                            {item.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
            )}
            {/* Sort By Filter */}
            <Grid size={12}>
              <Grid container spacing={1.25} flexWrap={'nowrap'} alignItems={'center'}>
                <Grid size="grow">
                  <Typography>סידור לפי</Typography>
                </Grid>
                <Grid>
                  <FormControl fullWidth size="small" sx={{ width: 100 }}>
                    <Select
                      value={filters.sortBy}
                      onChange={(e) => handleSortByFilter(e.target.value as 'lastUsed' | 'name')}
                    >
                      {filtersParams.sortBy.map((item) => (
                        <MenuItem value={item.value} key={item.value}>
                          {item.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Popover>
    </React.Fragment>
  );
};
export default TemplatesMenuHeader;
